import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * LearningSnapshot - Post-session investment visibility card
 *
 * Shows users what Mira learned about them after each session.
 * Makes the invisible investment visible to drive conversion.
 *
 * Design: Cash App simplicity + Discord dark theme
 */

// Track funnel actions for aggregated analytics (FERPA-compliant)
const trackFunnelAction = async (anonymousId, action) => {
  if (!anonymousId) return;
  try {
    await fetch('/api/guest/track-funnel-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anonymous_id: anonymousId,
        action: action,
        session_type: 'mira_guest'
      })
    });
  } catch (error) {
    console.debug('Funnel tracking error:', error);
    // Silent fail - don't interrupt user flow
  }
};

const LearningSnapshot = ({
  isOpen,
  onClose,
  onContinue,
  sessionNumber,
  totalFreeSessions,
  learningStyle,
  topicsCovered,
  cognitiveState,
  miraOrbColor,
  isAnonymous,
  onCreateAccount,
  onStartOver,
  anonymousId,  // Added for tracking
  // Accumulated investment data (persistence)
  totalSessions = 0,
  totalMinutes = 0,
  streakDays = 0,
  allTopicsCovered = []
}) => {
  if (!isOpen) return null;

  // Wrapped handlers that track before calling original callbacks
  const handleCreateAccount = () => {
    trackFunnelAction(anonymousId, 'learning_snapshot_create_account');
    onCreateAccount?.();
  };

  const handleStartOver = () => {
    trackFunnelAction(anonymousId, 'learning_snapshot_start_over');
    onStartOver?.();
  };

  const handleContinue = () => {
    trackFunnelAction(anonymousId, 'learning_snapshot_continue');
    (onContinue || onClose)?.();
  };

  // Determine which gate to show based on session number and user type
  const isLastAnonSession = isAnonymous && sessionNumber >= totalFreeSessions;
  const showCreateAccountPrompt = isLastAnonSession;

  // Learning style display
  const formatLearningStyle = (style) => {
    if (!style) return null;
    const styles = [];
    if (style.visual > 50) styles.push({ name: 'Visual', percent: style.visual });
    if (style.auditory > 50) styles.push({ name: 'Auditory', percent: style.auditory });
    if (style.stepByStep > 50) styles.push({ name: 'Step-by-step', percent: style.stepByStep });
    return styles.length > 0 ? styles : [{ name: 'Still learning', percent: null }];
  };

  const styles = formatLearningStyle(learningStyle);

  // Cognitive state friendly names
  const cognitiveLabels = {
    'building_confidence': 'Building confidence',
    'confident': 'Confident',
    'progressing': 'Making progress',
    'struggling': 'Working through challenges',
    'mastering': 'Mastering concepts',
    'flow_state': 'In the zone'
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && !showCreateAccountPrompt && onClose()}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          style={{
            backgroundColor: '#1c1c1e',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '400px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px 24px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 600,
              color: '#ffffff',
              letterSpacing: '-0.3px'
            }}>
              {showCreateAccountPrompt ? 'Your Learning Profile' : 'Session Complete'}
            </h2>
            {!showCreateAccountPrompt && (
              <p style={{
                margin: '8px 0 0',
                fontSize: '14px',
                color: '#8e8e93'
              }}>
                Mira is learning how you think
              </p>
            )}
          </div>

          {/* Content */}
          <div style={{ padding: '20px 24px' }}>
            {/* What Mira learned */}
            {(styles && styles[0]?.percent) && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{
                  fontSize: '13px',
                  color: '#8e8e93',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Learning Style Detected
                </p>
                {styles.map((style, idx) => (
                  <div key={idx} style={{ marginBottom: '8px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#ffffff' }}>
                        {style.name}
                      </span>
                      {style.percent && (
                        <span style={{ fontSize: '14px', color: '#8e8e93' }}>
                          {Math.round(style.percent)}%
                        </span>
                      )}
                    </div>
                    {style.percent && (
                      <div style={{
                        height: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${style.percent}%` }}
                          transition={{ duration: 0.8, delay: 0.2 }}
                          style={{
                            height: '100%',
                            background: 'linear-gradient(90deg, #297A92, #42909E)',
                            borderRadius: '2px'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Topics covered */}
            {topicsCovered && topicsCovered.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{
                  fontSize: '13px',
                  color: '#8e8e93',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Topics Covered
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {topicsCovered.map((topic, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'rgba(41, 122, 146, 0.2)',
                        color: '#6DB4CD',
                        borderRadius: '100px',
                        fontSize: '13px'
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Cognitive state */}
            {cognitiveState && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{
                  fontSize: '13px',
                  color: '#8e8e93',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Current State
                </p>
                <p style={{
                  fontSize: '15px',
                  color: '#30d158',
                  margin: 0
                }}>
                  {cognitiveLabels[cognitiveState] || 'Learning'}
                </p>
              </div>
            )}

            {/* Session counter */}
            <div style={{
              padding: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '14px', color: '#8e8e93' }}>
                  Free sessions
                </span>
                <span style={{ fontSize: '14px', color: '#ffffff', fontWeight: 500 }}>
                  {sessionNumber} of {totalFreeSessions} used
                </span>
              </div>
              {/* Progress dots */}
              <div style={{
                display: 'flex',
                gap: '6px',
                marginTop: '12px',
                justifyContent: 'center'
              }}>
                {Array.from({ length: totalFreeSessions }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: idx < sessionNumber ? '#297A92' : 'rgba(255, 255, 255, 0.2)'
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Create Account Prompt (for last anonymous session) */}
            {showCreateAccountPrompt && (
              <div style={{
                padding: '16px',
                backgroundColor: 'rgba(41, 122, 146, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(41, 122, 146, 0.3)',
                marginBottom: '16px'
              }}>
                {/* Investment Summary - Loss aversion framing */}
                {(totalMinutes > 0 || allTopicsCovered.length > 0) && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: totalMinutes > 0 && allTopicsCovered.length > 0 ? '1fr 1fr' : '1fr',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '8px'
                  }}>
                    {totalMinutes > 0 && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#a78bfa' }}>
                          {totalMinutes}
                        </div>
                        <div style={{ fontSize: '11px', color: '#8e8e93' }}>
                          minutes invested
                        </div>
                      </div>
                    )}
                    {allTopicsCovered.length > 0 && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#6DB4CD' }}>
                          {allTopicsCovered.length}
                        </div>
                        <div style={{ fontSize: '11px', color: '#8e8e93' }}>
                          topics explored
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Streak indicator if applicable */}
                {streakDays > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, rgba(255, 159, 67, 0.15), rgba(255, 183, 107, 0.15))',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 159, 67, 0.3)'
                  }}>
                    <span style={{ fontSize: '20px' }}>🔥</span>
                    <span style={{ fontSize: '14px', color: '#FF9F43', fontWeight: 600 }}>
                      {streakDays} day streak - don't lose it!
                    </span>
                  </div>
                )}

                <p style={{
                  fontSize: '14px',
                  color: '#ffffff',
                  margin: '0 0 12px',
                  lineHeight: 1.5
                }}>
                  Create a free account to:
                </p>
                <ul style={{
                  margin: 0,
                  padding: '0 0 0 20px',
                  color: '#8e8e93',
                  fontSize: '13px',
                  lineHeight: 1.8
                }}>
                  <li style={{ color: '#30d158' }}>Save this progress</li>
                  <li style={{ color: '#30d158' }}>Get 4 more free sessions</li>
                  <li style={{ color: '#30d158' }}>See friends studying now</li>
                </ul>
              </div>
            )}
          </div>

          {/* Footer / Actions */}
          <div style={{
            padding: '16px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {showCreateAccountPrompt ? (
              <>
                <button
                  onClick={handleCreateAccount}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: '#297A92',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1e5f73'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#297A92'}
                >
                  Create Free Account - Keep My Progress
                </button>
                <button
                  onClick={handleStartOver}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'transparent',
                    color: '#8e8e93',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Start Over as New Guest
                </button>
              </>
            ) : (
              <button
                onClick={handleContinue}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#297A92',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1e5f73'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#297A92'}
              >
                {sessionNumber < totalFreeSessions
                  ? 'Continue Learning'
                  : 'Got It'
                }
              </button>
            )}

            {/* Teacher free lane — end-of-session is the highest-intent
                conversion moment; teachers should see the free classroom
                path here, not only paid/account options. */}
            <a
              href="/login?role=teacher"
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '10px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#297A92',
                textDecoration: 'none',
                border: '1px dashed rgba(41, 122, 146, 0.4)',
                borderRadius: '10px'
              }}
            >
              🍎 Teaching a class? Get this free for all 30 of your students →
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LearningSnapshot;
